import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Play, Trash2 } from 'lucide-react';
import aiService from '../../services/aiService';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionType: string;
  actionName: string;
  defaultPrompt: string;
  userId: string;
  clientId: string | null;
}

export default function QuickActionScheduleDialog({
  open,
  onOpenChange,
  actionType,
  actionName,
  defaultPrompt,
  userId,
  clientId,
}: Props) {
  const [frequency, setFrequency] = useState('WEEKLY');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [timeOfDay, setTimeOfDay] = useState('06:00');
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [format, setFormat] = useState('detailed_report');
  const [schedules, setSchedules] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  const loadSchedules = async () => {
    if (!userId) return;
    setLoadingSchedules(true);
    try {
      const resp: any = clientId
        ? await aiService.getQuickActionSchedulesByClient(userId, clientId)
        : await aiService.getQuickActionSchedules(userId);
      const list = resp.data || resp || [];
      setSchedules(list.filter((s: any) => s.actionType === actionType));
    } catch (e) {
      console.error('Error loading schedules:', e);
    } finally {
      setLoadingSchedules(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPrompt(defaultPrompt);
      loadSchedules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, actionType, defaultPrompt]);

  const createSchedule = async () => {
    setSaving(true);
    try {
      const payload: any = {
        userId,
        clientId: clientId || null,
        actionType,
        frequency,
        timeOfDay,
        prompt,
        format,
        timezone: 'UTC',
      };
      if (frequency === 'WEEKLY') payload.dayOfWeek = Number(dayOfWeek);
      if (frequency === 'MONTHLY') payload.dayOfMonth = Number(dayOfMonth);
      await aiService.createQuickActionSchedule(payload);
      await loadSchedules();
    } catch (e) {
      console.error('Error creating schedule:', e);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id: string, isActive: boolean) => {
    try {
      await aiService.toggleQuickActionSchedule(id, !isActive);
      await loadSchedules();
    } catch (e) {
      console.error(e);
    }
  };

  const remove = async (id: string) => {
    try {
      await aiService.deleteQuickActionSchedule(id);
      await loadSchedules();
    } catch (e) {
      console.error(e);
    }
  };

  const runNow = async (id: string) => {
    try {
      await aiService.runQuickActionSchedule(id);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">Schedule: {actionName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Frequency</label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DAILY">Daily</SelectItem>
                <SelectItem value="WEEKLY">Weekly</SelectItem>
                <SelectItem value="MONTHLY">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {frequency === 'WEEKLY' && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Day of week</label>
              <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {frequency === 'MONTHLY' && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Day of month</label>
              <Input
                type="number"
                min={1}
                max={28}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Time (UTC)</label>
            <Input type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              className="w-full rounded-lg border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Report Format</label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Plain Text</SelectItem>
                <SelectItem value="bullet_points">Bullet Points</SelectItem>
                <SelectItem value="table">Table</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="detailed_report">Detailed Report</SelectItem>
                <SelectItem value="executive_summary">Executive Summary</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={createSchedule} disabled={saving} size="sm">
            {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Add Schedule
          </Button>

          {schedules.length > 0 && (
            <div className="space-y-2 border-t pt-3">
              <div className="text-xs font-semibold text-muted-foreground">
                Existing Schedules ({schedules.length})
              </div>
              {schedules.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg border p-2 text-xs">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium">
                      <span className="capitalize">{s.frequency?.toLowerCase()}</span>
                      {s.frequency === 'WEEKLY' && s.dayOfWeek != null && (
                        <span className="text-muted-foreground">
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][s.dayOfWeek - 1]}
                        </span>
                      )}
                      {s.frequency === 'MONTHLY' && s.dayOfMonth != null && (
                        <span className="text-muted-foreground">Day {s.dayOfMonth}</span>
                      )}
                      <span className="text-muted-foreground">at {s.timeOfDay} UTC</span>
                    </div>
                    <div className="text-muted-foreground">
                      Format: <span className="capitalize">{s.format?.replace(/_/g, ' ') || 'detailed report'}</span>
                    </div>
                    <div className="text-muted-foreground">
                      Next: {s.nextRunAt ? new Date(s.nextRunAt + 'Z').toLocaleString() : '—'}
                    </div>
                    {s.lastRunAt && (
                      <div className="text-muted-foreground">
                        Last run: {new Date(s.lastRunAt + 'Z').toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-6 p-1" onClick={() => runNow(s.id)} title="Run now" disabled={!s.isActive}>
                      <Play className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 p-1"
                      onClick={() => toggle(s.id, s.isActive)}
                      title={s.isActive ? 'Disable' : 'Enable'}
                    >
                      {s.isActive ? 'On' : 'Off'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 p-1 text-rose-500"
                      onClick={() => remove(s.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loadingSchedules && schedules.length === 0 && (
            <div className="text-xs text-muted-foreground">
              No schedules for this quick action yet. Add one above.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}